import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Card, CardBody, Container } from "reactstrap";

import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import styles from "Pages/Setup/TestConnection/TestConnection.module.css";
import ReactTable from "components/ReactTable/ReactTable";
import EditModal from "Pages/Setup/TestConnection/Components/EditModal/EditModal";
import Test from "Pages/Setup/TestConnection/Components/Test/Test";
import { HideOrShowPassword } from "Pages/Setup/Configuration/Components/HideOrShowPassword/HideOrShowPassword";
import { isHidden } from "Pages/Setup/Configuration/Configuration";
import usePageTitle from "Hooks/usePageTitle";
type ActionType = "new" | "edit" | "delete" | "";

interface ITestData {
    system: string;
    tests: string[];
    configurations: {
        keyName: string;
        keyValue: string;
    }[];
}

export interface IObject {
    [key: string]: any;
}

export default function TestConnection() {
    /** Holds info about systems, available tests, and configurations for the system */
    const [testData, setTestData] = useState<ITestData[] | undefined>(
        undefined
    );
    const [isLoading] = useState(false);
    /** Holds info about the row that is selected in the table. Used for editing modal */
    const [selectedRow, setSelectedRow] = useState<any>(undefined);
    /** Responsible for toggling the modal */
    const [activeModal, setActiveModal] = useState<ActionType>("");

    usePageTitle("Test Connection");
    const { get } = useFetch();
    const { newAlert } = useAlertContext();

    // Gets test data (system, tests, and configurations for system) on page load
    useEffect(() => {
        let ignore = false;

        getTests();

        async function getTests() {
            const response = await get("/api/test_connection");

            let data: Response | ITestData[];
            try {
                data = await response.json();
            } catch (e) {
                data = response;
            }
            console.log("TestConnection test data response", data);

            if (response.ok && !ignore) {
                setTestData(data as ITestData[]);
            } else {
                newAlert({ message: "Error getting tests" });
            }
        }
    }, [get, newAlert]);

    /** Wraps state setting callback to pass to child component and update state from parent */
    const onSetActiveModal = useCallback((modal: ActionType) => {
        setActiveModal(modal);
    }, []);

    /** Wraps state setting callback to pass to child component and update state from parent */
    function onSetSelectedRow(row: any) {
        setSelectedRow(row);
    }

    /** Passed down to table as rowDoubleClickCallback */
    const editRow = useCallback((row: any) => {
        setActiveModal("edit");
        setSelectedRow(row);
    }, []);

    /** Will update UI after successful fetch*/
    const updateConfigs = (data: { keyName: string; keyValue: string }) => {
        const { keyName, keyValue } = data;
        const system = selectedRow.original.keyName.split(".")[0].toLowerCase();

        // Update testData state, but only for the system and key that was changed
        const newTestData = testData?.map((testDataObj) => {
            if (testDataObj.system.toLowerCase() === system) {
                const newConfiguration = testDataObj.configurations.map(
                    (config) => {
                        if (config.keyName === keyName) {
                            return {
                                ...config,
                                keyValue,
                            };
                        }
                        return config;
                    }
                );
                return {
                    ...testDataObj,
                    configurations: newConfiguration,
                };
            }
            return testDataObj;
        });
        setTestData(newTestData);
    };

    const columns = useMemo(() => {
        return [
            {
                Header: "Configuration",
                accessor: "keyName",
                disableFilters: true,
                disableSortBy: true,
            },
            {
                accessor: "keyValue",
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ value, row }: { value: string; row: any }) =>
                    isHidden(row)
                        ? HideOrShowPassword(value, row.index)
                        : value,
            },
        ];
    }, []);

    // Data prop is passed inside the JSX below since it is dependent on parameter of a map function
    const tableProps = {
        columns,
        isLoading,
        showExportCsv: false,
        rowDoubleClickCallback: editRow,
    };

    const modalProps = {
        activeModal,
        updateConfigs,
        onSetActiveModal,
        selectedRow,
        onSetSelectedRow,
    };

    return (
        <Container fluid>
            <h1>Test Connection</h1>
            {/* No tests found */}
            {testData !== undefined && !testData.length && (
                <Alert color="danger">No tests found for this company</Alert>
            )}
            {/* Tests found */}
            {testData?.length &&
                testData.map(({ system, tests, configurations }) => (
                    // System cards
                    <Card key={system} className={styles.systemBox}>
                        <CardBody>
                            <h4>{system}</h4>

                            {/* Box holding test cards */}
                            <div className={styles.testsBox}>
                                {/* Test cards */}
                                {tests.map((test: string) => (
                                    <Test
                                        key={system + test}
                                        system={system}
                                        test={test}
                                    />
                                ))}{" "}
                                {/* Configurations */}
                                {configurations.length ? (
                                    <div className={styles.tableBox}>
                                        <ReactTable
                                            data={configurations}
                                            {...tableProps}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </CardBody>
                    </Card>
                ))}
            <EditModal {...modalProps} />
        </Container>
    );
}
